// Firebase Cloud Messaging helpers for the PWA (required for iOS home-screen notifications).
// The notification display/click handling lives in /public/service-worker.js.

import {initializeApp} from 'firebase/app';
import {
  Messaging,
  getMessaging,
  onRegistered,
  onUnregistered,
  register,
  unregister
} from 'firebase/messaging';

import {APP_VARS} from '../constants';

const firebaseConfig = APP_VARS.FIREBASE;

const isConfigured = Boolean(
  firebaseConfig?.API_KEY &&
    firebaseConfig?.PROJECT_ID &&
    firebaseConfig?.MESSAGING_SENDER_ID &&
    firebaseConfig?.APP_ID &&
    firebaseConfig?.VAPID_KEY
);

let messaging: Messaging | null = null;

const getMessagingInstance = (): Messaging => {
  if (!messaging) {
    const app = initializeApp({
      apiKey: firebaseConfig?.API_KEY,
      projectId: firebaseConfig?.PROJECT_ID,
      messagingSenderId: firebaseConfig?.MESSAGING_SENDER_ID,
      appId: firebaseConfig?.APP_ID
    });
    messaging = getMessaging(app);
  }
  return messaging;
};

// Reuses the app's own /service-worker.js registration — no separate firebase-messaging-sw.js.
const getRegisterOptions = async () => ({
  vapidKey: firebaseConfig?.VAPID_KEY,
  serviceWorkerRegistration: await navigator.serviceWorker.ready
});

// register()/unregister() deliver the Firebase Installation ID (FID) through an
// observer instead of a return value — wrap the pair into a single promise.
const awaitFid = (
  instance: Messaging,
  observe: typeof onRegistered,
  action: () => Promise<void>
): Promise<string> =>
  new Promise((resolve, reject) => {
    const stop = observe(instance, {
      next: (fid) => {
        stop();
        resolve(fid);
      },
      error: (error) => {
        stop();
        reject(error);
      },
      complete: () => undefined
    });
    action().catch((error) => {
      stop();
      reject(error);
    });
  });

export const pushService = {
  isSupported: (): boolean =>
    isConfigured &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window,

  getSubscription: async (): Promise<PushSubscription | null> => {
    if (!pushService.isSupported()) {
      return null;
    }
    const registration = await navigator.serviceWorker.ready;
    return registration.pushManager.getSubscription();
  },

  // Requests permission (must run in a user gesture on iOS) and registers with FCM.
  // Returns the Firebase Installation ID (FID) to register on the backend, or
  // null if unsupported or permission was not granted.
  subscribe: async (): Promise<string | null> => {
    if (!pushService.isSupported()) {
      return null;
    }

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      return null;
    }

    const instance = getMessagingInstance();
    const options = await getRegisterOptions();
    try {
      return await awaitFid(instance, onRegistered, () => register(instance, options));
    } catch (error) {
      // A pre-Firebase (raw VAPID) subscription blocks FCM: drop it and retry once.
      const existing = await options.serviceWorkerRegistration.pushManager.getSubscription();
      if (!existing) {
        throw error;
      }
      await existing.unsubscribe();
      return awaitFid(instance, onRegistered, () => register(instance, options));
    }
  },

  // Unregisters from FCM and cancels the browser subscription. Returns the FID
  // so the backend can deregister the device.
  unsubscribe: async (): Promise<string | null> => {
    const subscription = await pushService.getSubscription();
    if (!subscription) {
      return null;
    }
    try {
      const instance = getMessagingInstance();
      const fid = await awaitFid(instance, onUnregistered, () => unregister(instance));
      await subscription.unsubscribe();
      return fid;
    } catch {
      // A pre-Firebase (raw VAPID) subscription: nothing to deregister, drop it locally.
      await subscription.unsubscribe();
      return null;
    }
  }
};
